import React, { useState } from 'react';
import { X, Save, Clock, Award, TrendingUp, Calendar } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import type { UserSkillWithMaster } from '@shared/types/skills-tracker.types';

interface ProgressLoggingModalProps {
  skill: UserSkillWithMaster | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ProgressEntry {
  hours_invested: number;
  activity_type: string;
  activity_description: string;
  achievements: string;
  confidence_level: number;
  progress_increment: number;
}

const activityTypes = [
  'Course/Tutorial',
  'Book Reading',
  'Project Work',
  'Practice/Exercises',
  'Mentoring Session',
  'Workshop/Conference',
  'Online Research',
  'Peer Collaboration',
  'Other'
];

export const ProgressLoggingModal: React.FC<ProgressLoggingModalProps> = ({
  skill,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<ProgressEntry>({
    hours_invested: 1,
    activity_type: 'Practice/Exercises',
    activity_description: '',
    achievements: '',
    confidence_level: 5,
    progress_increment: 5
  });

  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof ProgressEntry, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): string | null => {
    if (!formData.activity_description.trim()) {
      return 'Activity description is required';
    }

    if (formData.hours_invested <= 0) {
      return 'Hours invested must be greater than 0';
    }

    if (formData.progress_increment < 0 || formData.progress_increment > 100) {
      return 'Progress increment must be between 0 and 100';
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!skill) return;

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Calculate new progress percentage
      const newProgress = Math.min(100, skill.progress_percentage + formData.progress_increment);

      // Build update notes with all the information
      const updateNotes = [
        `Activity: ${formData.activity_type}`,
        `\n${formData.activity_description}`,
        formData.achievements ? `\n\nAchievements: ${formData.achievements}` : '',
        `\n\nConfidence Level: ${formData.confidence_level}/10`
      ].join('');

      // Insert progress update
      const { error: progressError } = await supabase
        .from('skill_progress_updates')
        .insert({
          user_skill_id: skill.id,
          user_id: user.id,
          update_type: 'progress_update',
          progress_delta: formData.progress_increment,
          previous_progress_percentage: skill.progress_percentage,
          new_progress_percentage: newProgress,
          update_notes: updateNotes,
          time_spent_hours: formData.hours_invested,
          update_source: 'user_manual'
        });

      if (progressError) throw progressError;

      // Update skill progress
      const { error: updateError } = await supabase
        .from('user_skills_tracker')
        .update({
          progress_percentage: newProgress,
          last_progress_update: new Date().toISOString()
        })
        .eq('id', skill.id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Check if skill is now completed
      if (newProgress === 100 && skill.progress_percentage < 100) {
        await supabase
          .from('user_skills_tracker')
          .update({ status: 'completed' })
          .eq('id', skill.id);

        toast.success('🎉 Skill completed! Congratulations!', { duration: 5000 });
      } else {
        toast.success('Progress logged successfully!');
      }

      resetForm();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error logging progress:', error);
      toast.error(error.message || 'Failed to log progress');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      hours_invested: 1,
      activity_type: 'Practice/Exercises',
      activity_description: '',
      achievements: '',
      confidence_level: 5,
      progress_increment: 5
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen || !skill) return null;

  const remainingProgress = 100 - skill.progress_percentage;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleClose} />

        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">Log Progress</h3>
              <p className="text-sm text-gray-600 mt-1">{skill.skill.skill_name}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Current Progress */}
          <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Current Progress</span>
              <span className="text-sm font-bold text-blue-600">{skill.progress_percentage}%</span>
            </div>
            <div className="w-full bg-white rounded-full h-3">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
                style={{ width: `${skill.progress_percentage}%` }}
              />
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {remainingProgress}% remaining to complete
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Hours and Progress */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4" />
                  Hours Invested
                </label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  max="24"
                  value={formData.hours_invested}
                  onChange={(e) => handleChange('hours_invested', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  Progress Increment (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max={remainingProgress}
                  value={formData.progress_increment}
                  onChange={(e) => handleChange('progress_increment', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  New progress: {Math.min(100, skill.progress_percentage + formData.progress_increment)}%
                </p>
              </div>
            </div>

            {/* Activity Type */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4" />
                Activity Type
              </label>
              <select
                value={formData.activity_type}
                onChange={(e) => handleChange('activity_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {activityTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Activity Description */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Activity Description *
              </label>
              <textarea
                value={formData.activity_description}
                onChange={(e) => handleChange('activity_description', e.target.value)}
                rows={4}
                placeholder="Describe what you learned or accomplished today..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.activity_description.length} characters
              </p>
            </div>

            {/* Achievements */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Award className="w-4 h-4" />
                Key Achievements
              </label>
              <textarea
                value={formData.achievements}
                onChange={(e) => handleChange('achievements', e.target.value)}
                rows={2}
                placeholder="Notable achievements, breakthroughs, or milestones reached..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Confidence Level */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Confidence Level: {formData.confidence_level}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.confidence_level}
                onChange={(e) => handleChange('confidence_level', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Not confident</span>
                <span>Moderately confident</span>
                <span>Very confident</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Logging...' : 'Log Progress'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
